(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  if (!global.NewHomeSource.Helpers ||
    !global.NewHomeSource.Helpers.Array) {
    return;
  }

  const arrayHelper = global.NewHomeSource.Helpers.Array;

  class SlidesToScroll {
    /**
     * Initializes a new instance of the class.
     * Sets the initial state to null.
     */
    constructor() {
      this.state = null;
    }

    /**
     * Initializes the object with the provided state.
     *
     * @param {Object} state - The state object to initialize with.
     * @returns {this} The current instance for chaining.
     */
    initialize(state) {
      this.state = state;
      return this;
    }

    /**
     * Groups an array of slide indices into chunks based on their size and the visible view size.
     * Determines how many slides should be scrolled at once in a carousel, considering axis, container size,
     * slide rectangles, looping, gaps, and pixel tolerance.
     *
     * @param {Array} array - The array of slide indices to group.
     * @returns {Array<Array>} An array of arrays, where each sub-array contains the indices of slides that
     * fit in one scroll group.
     */
    bySize(array) {
      if (!array.length) {
        return [];
      }
      const axis = this.state.get('axis');
      const containerRect = this.state.get('containerRect');
      const viewSize = this.state.get('viewSize');
      const slideRects = this.state.get('slideRects');
      const loop = this.state.get('loop');
      const gap = this.state.get('edgeGap');
      const pixelTolerance = this.state.get('pixelTolerance');
      const startEdge = axis.getStartEdge();
      const endEdge = axis.getEndEdge();

      return arrayHelper.arrayKeys(array).reduce((groups, rectB, index) => {
        const rectA = arrayHelper.arrayLast(groups) || 0;
        const isLast = rectB === arrayHelper.arrayLastIndex(array);
        const isEdge = rectA === 0 || isLast;
        const edgeA = containerRect[startEdge] - slideRects[rectA][startEdge];
        const edgeB = containerRect[startEdge] - slideRects[rectB][endEdge];
        const gapDirection = !loop && isEdge ? axis.direction(gap) : 0;
        const chunkSize = Math.abs(edgeB - gapDirection - (edgeA + gapDirection));

        if (index && chunkSize > viewSize + pixelTolerance) {
          groups.push(rectB);
        }

        if (isLast) {
          groups.push(array.length);
        }

        return groups;
      }, []).map((currentSize, index, groups) => {
        const previousSize = Math.max(groups[index - 1] || 0);
        return array.slice(previousSize, currentSize);
      });
    }

    /**
     * Groups an array of slides based on the current `slidesToScroll` state.
     * If `slidesToScroll` is a valid number, groups slides using `SlidesToScroll.byNumber`.
     * Otherwise, groups slides using the `bySize` method.
     *
     * @param {Array} array - The array of slides to group.
     * @returns {Array} The grouped slides.
     */
    groupSlides(array) {
      const slidesToScroll = this.state.get('slidesToScroll');
      return !Number.isNaN(slidesToScroll) ? SlidesToScroll.byNumber(array, slidesToScroll) : this.bySize(array);
    }

    /**
     * Splits an array into groups of a specified size.
     *
     * @param {Array} array - The array to be split into groups.
     * @param {number} groupSize - The size of each group.
     * @returns {Array<Array>} An array of arrays, where each sub-array contains up to groupSize elements
     * from the original array.
     */
    static byNumber(array, groupSize) {
      return arrayHelper.arrayKeys(array)
        .filter((index) => index % groupSize === 0)
        .map((index) => array.slice(index, index + groupSize));
    }
  }

  global.NewHomeSource.Carousel.SlidesToScroll = SlidesToScroll;
}(window));
